import { Input } from '@ohear/ui'
import { type FormEvent, useState } from 'react'
import { Panel, SlpButton } from '../components/slp'
import { authClient } from '../lib/better-auth'
import { navigate } from '../lib/router'

/**
 * /reset-password — landing for Better Auth password-recovery emails.
 * Better Auth verifies the emailed link, then redirects here with a one-time
 * `?token=` value consumed by the reset request.
 */

type Phase = 'ready' | 'no-link' | 'done'

export function ResetPasswordPage() {
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token'))
  const [phase, setPhase] = useState<Phase>(() => (token ? 'ready' : 'no-link'))
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Passwords need at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    setBusy(true)
    const { error: err } = await authClient.resetPassword({
      newPassword: password,
      token: token ?? undefined,
    })
    setBusy(false)
    if (err) {
      setError(err.message ?? 'Could not update the password.')
      return
    }
    setPhase('done')
  }

  // Desktop-locked SLP shell (1180px) — matches AuthScreen / the /join error
  // card (W1 S1). DesktopGate intercepts <900px, so this only renders on desktop.
  return (
    <div className="min-h-screen min-w-[1180px] bg-[var(--slp-page)]">
      <div className="mx-auto flex min-h-screen w-[1180px] flex-col justify-center">
        <div className="mx-auto w-[472px]">
          <h1 className="mb-1 text-[28px] font-semibold tracking-tight text-[var(--slp-text)]">
            OHEAR Session
          </h1>
          <p className="mb-6 text-[14px] text-[var(--slp-text-dim)]">Set a new password.</p>

          <Panel title="Reset password">
            {phase === 'no-link' ? (
              <div className="flex flex-col gap-3" data-testid="rt-reset-no-link">
                <p className="text-[13px] text-[var(--slp-text)]">
                  This page sets a new password after you arrive from the reset email. No reset link
                  detected — request one from the sign-in screen.
                </p>
                <SlpButton variant="secondary" onClick={() => navigate('/new')}>
                  Back to sign in
                </SlpButton>
              </div>
            ) : null}

            {phase === 'ready' ? (
              <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-3">
                <Input
                  type="password"
                  placeholder="New password (min 8 characters)"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="rt-reset-password"
                />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="rt-reset-confirm"
                />
                {error ? <p className="text-[13px] text-[var(--amber)]">{error}</p> : null}
                <SlpButton type="submit" disabled={busy} data-testid="rt-auth-reset-submit">
                  {busy ? 'Updating…' : 'Set new password'}
                </SlpButton>
              </form>
            ) : null}

            {phase === 'done' ? (
              <div className="flex flex-col gap-3" data-testid="rt-reset-done">
                <p className="text-[13px] text-[var(--slp-text)]">
                  Password updated. Your old sessions were signed out for security.
                </p>
                <SlpButton onClick={() => navigate('/new')}>Go to sessions</SlpButton>
              </div>
            ) : null}
          </Panel>
        </div>
      </div>
    </div>
  )
}
